// [목적] /chat — 좌측 채팅(RAG 대화) / 우측 생성 진행 상태 패널
// [이유] Spec(docs/specs/Approved/2026-07-05_fe-rag-generate-rewire.md) §7 Resolved Decisions(2026-07-19):
//        죽은 /plan API(usePlanSession/usePlanJob/usePlanGenerateStream/usePlanSlotFilling) 배선을
//        실존하는 /rag API(createConversation/turn/submitSlots/generation 폴링)로 전면 교체한다.
//        네트워크·상태 로직은 useChatFlow 훅(hooks/useChatFlow.ts)에 위임 — 이 페이지는 그 결과를
//        기존 UI 언어(봇/유저 버블, confirm 2액션 칩, useAuthGate 로그인 유도)로 렌더링만 한다.
//        구 SlotFillingState(slots/followUpQuestions/slotProgress 등)에 묶인 로직(TripInfoCard·
//        PreviewMiniCard·ChatBubbleStreaming·KnowledgeAnswerBubble·RefusalBubble·quiet 필터·
//        SSE 진행률·?sid 새로고침 복구)은 신 계약에 대응 필드가 없어 전부 제거됨(D-1, D-3~D-8 불요화).
// [사이드 이팩트] useChatFlow → rag.client(createConversation/turn/submitSlots) + useFolioGeneration 폴링.
//                useAuthGate('chat.create') 로 send()/confirmGenerate() 진입을 계속 가드(D-9 유지).
//                fe-home-chat-prefill: useSearchParams 추가 — HomePage.tsx 의 navigate(`/chat?q=...`)
//                입력값을 입력창 시드로 복원(자동 전송 없음, 마운트 시 1회만 사용).
// [수정 시 고려사항] IME 가드(e.nativeEvent.isComposing) 보존 필수 — 제거 시 "30만원" 분리 전송 버그 재발.
//                   PDF 다운로드는 buildPdfUrl(convId) 로 새 탭 네비게이션 — blob 변환 없음(ponytail, rag.client 주석 참조).
//                   ChatMobile 은 이 파일과 별개 컴포넌트 — useAuthGate 미적용 범위(D-9, 기존 범위 유지).
//                   [G1-1, 2026-09-17] pdf-ready 버블에 msg.portfolioId 기반 "폴리오 보기"(/folio/:id) 주 버튼
//                   추가 — PDF 다운로드는 부가 버튼으로 유지, portfolioId null 이면 기존 PDF 버튼만 노출.
//                   [FlowLine 83963a6b, 2026-09-18] 첫 진입 추천 칩을 mocks/fixtures/design-mock.ts
//                   SUGGEST_CHIPS 하드코딩 상수에서 GET /rag/concepts 실데이터(useConcepts)로 교체.
//                   로딩 중 스켈레톤, 실패/빈 배열 시 칩 영역 전체 숨김(HomePage.tsx showFestivalSection 과
//                   동일 패턴) — 채팅 자체 흐름은 칩 유무와 무관하게 정상 동작.
//                   [trip-guide, 2026-09-21] 첫 진입(대화 시작 전)에는 좌측을 단계형 가이드(TripGuide)로,
//                   우측을 단계별 미리보기(GuidePreview)로 대체한다. 사용자 피드백 "방향성이 없어 쓰기 어렵다" —
//                   지역/동반자·인원/이동 배려/기간/무드/예산을 순서대로 묻고 한 문장으로 조립해 입력창에 채운다.
//                   가이드는 send() 를 직접 호출하지 않는다(자동 전송 금지) — 기존 전송 경로(authGuard→flow.send)
//                   와 IME 가드를 그대로 쓰기 위해 입력창 값만 채우고 사용자가 전송 버튼을 누른다.
//                   '그냥 한 줄로 쓸래요' 로 기존 자유 입력(추천 칩) 화면으로 전환 가능.

import {
  Box,
  ButtonBase,
  LinearProgress,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { buildPdfUrl } from '../api/rag.client.js';
import type { Concept } from '../api/concepts.client.js';
import { TopBar } from '../components/layout/TopBar.js';
import { LoginPromptModal } from '../components/auth/LoginPromptModal.js';
import { useShell } from '../hooks/useShell.js';
import { useAuthGate } from '../hooks/useAuthGate.js';
import { useTypewriter } from '../hooks/useTypewriter.js';
import { useChatFlow, type ChatMsg } from '../hooks/useChatFlow.js';
import { useConcepts } from '../hooks/useConcepts.js';
import { TripGuide } from '../components/chat/guide/TripGuide.js';
import { GuidePreview } from '../components/chat/guide/GuidePreview.js';
import {
  GUIDE_STEPS,
  isAnswered as isGuideStepAnswered,
  type GuideAnswers,
} from '../components/chat/guide/guideSteps.js';

import { ChatMobile } from './mobile/ChatMobile.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
// 소형 브랜드 텍스트(11~14px) — brand[500] 은 소형에서 WCAG AA 미달(packages/ui tokens/color.ts 주석)
const BRAND_TEXT = c.brand[600][L];
const SHADOW_CARD = tokens.shadow.card;

// [목적] 생성 잡 단계(stage) → 한글 라벨 매핑
// [이유] BE GenerationStageEnum(extract/search/generate/render) 을 사용자 문구로 변환.
// [사이드 이팩트] 없음
// [수정 시 고려사항] BE stage 값 추가/변경 시 동기 갱신 필요(rag.client.ts GenerationStageEnum 참조).
const STAGE_LABELS: Record<string, string> = {
  extract: '여행 정보 정리 중...',
  search: '장소 탐색 중...',
  generate: '이야기 생성 중...',
  render: 'PDF 만드는 중...',
};

// [목적] 생성 단계 스텝퍼 정의 — 완료/진행/대기 3상태를 한 화면에 보여주기 위한 순서와 라벨
// [이유] [2026-09-20] 기존 생성 화면은 "이야기 생성 중..." 한 줄 + 무한 프로그레스바가 전부라,
//        50초 가까운 대기 동안 무엇이 얼마나 남았는지 알 수 없어 멈춘 것처럼 보였다.
//        BE 는 이미 stage(extract/search/generate/render) 를 내려주고 있으므로, 그걸 4단계 체크리스트로
//        펼쳐 "어디까지 왔는지"를 시각화한다.
// [사이드 이팩트] 없음(표시 전용). STAGE_LABELS 와 키 집합이 같아야 한다.
// [수정 시 고려사항] BE GenerationStageEnum 에 단계가 추가되면 이 배열도 함께 갱신할 것 —
//                   누락되면 해당 단계에서 스텝퍼가 통째로 '대기'로 보인다.
const STAGE_STEPS: ReadonlyArray<{ key: string; label: string; hint: string }> = [
  { key: 'extract', label: '여행 정보 정리', hint: '기간·이동수단·예산을 정리하고 있어요' },
  { key: 'search', label: '장소 탐색', hint: '조건에 맞는 장소를 고르고 있어요' },
  { key: 'generate', label: '이야기 생성', hint: '하루 동선과 글을 쓰고 있어요' },
  { key: 'render', label: 'PDF 만들기', hint: '인쇄용 폴리오로 옮기고 있어요' },
];

/** [목적] 우측 패널 실제 대화 스택 1건 — 사용자 발화 + (도착했다면) 봇 응답 */
interface ConversationTurn {
  request: string;
  answer: string | null;
}

/**
 * [목적] flow.msgs(ChatMsg[]) 를 me→bot 짝 단위로 묶어 우측 패널 스택 데이터로 변환
 * [이유] 상단 conversationTurns useMemo 주석 참조 — opaque state 파싱 대신 이미 안전하게 타입화된
 *        ChatMsg[] 만 사용. clarify/confirm/pdf-ready/pdf-failed/session-expired 등 role 은 request 로
 *        (role==='me' 인 사용자 발화만 턴의 시작점으로 삼음).
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] 같은 me 다음에 오는 첫 bot 텍스트만 answer 로 채택 — 그 사이 clarify 칩 등은 무시.
 */
function buildTurnStack(msgs: ChatMsg[]): ConversationTurn[] {
  const turns: ConversationTurn[] = [];
  for (let i = 0; i < msgs.length; i += 1) {
    const m = msgs[i];
    if (!m || m.role !== 'me') continue;
    let answer: string | null = null;
    for (let j = i + 1; j < msgs.length; j += 1) {
      const next = msgs[j];
      if (!next || next.role === 'me') break;
      if (next.role === 'bot' && next.text) {
        answer = next.text;
        break;
      }
    }
    turns.push({ request: m.text, answer });
  }
  return turns;
}

/**
 * [목적] 폴리오 생성 대기 화면 — 4단계 스텝퍼 + 결정적 진행바 + 결과 스켈레톤
 * [이유] [2026-09-20] 생성에 40~60초가 걸리는데 화면에는 한 줄 문구와 무한 프로그레스바뿐이라
 *        진행 상황을 알 수 없었다("멈춘 건가?"). BE 가 내려주는 stage 를 체크리스트로 펼쳐
 *        완료/진행/대기를 구분하고, 진행바도 단계 기반 determinate 로 바꿔 남은 양을 보여준다.
 *        아래 스켈레톤은 "무엇이 만들어지는 중인지"(Day 카드가 쌓이는 중)를 형태로 암시한다.
 * [사이드 이팩트] 없음(표시 전용).
 * [수정 시 고려사항] stage 가 STAGE_STEPS 에 없는 값이면 전부 '대기'로 보이므로 BE enum 과 동기 유지.
 *                   aria-live 는 현재 단계 텍스트 한 곳에만 둔다 — 여러 곳에 두면 스크린리더가 중복 낭독한다.
 */
/**
 * [목적] 가이드 흐름 완료 패널 — 우측에 결과를 크게 보여준다(폴리오 보기 / PDF 다운로드)
 * [이유] [2026-09-21] 완료 알림이 좌측 채팅 자리에 작은 말풍선으로 나와, 50초 기다린 결과치고는
 *        존재감이 없었다(사용자 피드백: "하단에 저렇게 뿌리지 말고 오른쪽에 이쁘게").
 *        우측은 생성 진행을 보던 자리라 시선이 이미 가 있어, 진행 → 완료가 같은 자리에서 이어진다.
 * [사이드 이팩트] 없음(표시 + 링크). 실패 시에는 재시도 버튼을 노출한다.
 * [수정 시 고려사항] msg.role 이 'pdf-failed' 인 경우도 이 패널이 받는다 — 성공/실패를 한 컴포넌트에서
 *                   분기해 완료 화면의 레이아웃(카드 위치/여백)이 두 경우에 어긋나지 않게 한다.
 */
function GuideResultPanel({
  msg,
  convId,
  onRetry,
  onRestart,
}: {
  msg: ChatMsg;
  convId: string | null;
  onRetry: () => void;
  /** 처음부터 다시 — 세션과 가이드 답변을 모두 비운다 */
  onRestart: () => void;
}): JSX.Element {
  const failed = msg.role === 'pdf-failed';

  return (
    <Box
      sx={{
        borderRadius: `${tokens.radius.l}px`,
        border: `1px solid ${BORDER}`,
        bgcolor: PAPER0,
        overflow: 'hidden',
        boxShadow: SHADOW_CARD,
        animation: 'folioResultIn .5s cubic-bezier(.2,.8,.25,1) both',
        '@keyframes folioResultIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'none' },
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          height: 4,
          background: failed
            ? c.danger[500][L]
            : `linear-gradient(90deg, ${BRAND}, ${c.accent[500][L]})`,
        }}
      />
      <Box sx={{ p: `${tokens.spacing[6]}px ${tokens.spacing[5]}px`, textAlign: 'center' }}>
        {/* 완료 마크 — 원이 튀어나오며 체크가 뒤따른다 */}
        <Box
          aria-hidden
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            fontWeight: 800,
            color: PAPER0,
            bgcolor: failed ? c.danger[500][L] : c.accent[600][L],
            animation: 'folioMarkPop .45s cubic-bezier(.34,1.56,.64,1) .08s both',
            '@keyframes folioMarkPop': {
              from: { transform: 'scale(.4)', opacity: 0 },
              to: { transform: 'scale(1)', opacity: 1 },
            },
          }}
        >
          {failed ? '!' : '✓'}
        </Box>

        <Typography
          sx={{
            mt: `${tokens.spacing[3]}px`,
            fontSize: 17,
            fontWeight: 800,
            color: INK_HI,
            letterSpacing: '-0.4px',
          }}
        >
          {failed ? '폴리오를 만들지 못했어요' : '폴리오가 완성됐어요'}
        </Typography>
        <Typography
          sx={{ mt: '6px', fontSize: 12, color: INK_MID, lineHeight: 1.7, whiteSpace: 'pre-line' }}
        >
          {failed
            ? msg.text
            : '하루 동선과 장소 정보, 예산까지 한 권으로 정리했어요.\n바로 열어보거나 PDF로 받아보세요.'}
        </Typography>

        <Stack
          direction="row"
          spacing={`${tokens.spacing[2]}px`}
          justifyContent="center"
          sx={{ mt: `${tokens.spacing[5]}px`, flexWrap: 'wrap' }}
        >
          {failed ? (
            <ButtonBase
              onClick={onRetry}
              sx={{
                px: `${tokens.spacing[5]}px`,
                minHeight: 42,
                borderRadius: `${tokens.radius.xl}px`,
                bgcolor: BRAND,
                color: PAPER0,
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              다시 시도
            </ButtonBase>
          ) : (
            <>
              {msg.portfolioId && (
                <ButtonBase
                  component={RouterLink}
                  to={`/folio/${msg.portfolioId}`}
                  aria-label="완성된 폴리오 보기"
                  sx={{
                    px: `${tokens.spacing[5]}px`,
                    minHeight: 42,
                    borderRadius: `${tokens.radius.xl}px`,
                    bgcolor: BRAND,
                    color: PAPER0,
                    fontSize: 13,
                    fontWeight: 800,
                    '&:focus-visible': {
                      outline: `2px solid ${BRAND}`,
                      outlineOffset: `${tokens.spacing[1]}px`,
                    },
                  }}
                >
                  폴리오 보기
                </ButtonBase>
              )}
              {convId && (
                <ButtonBase
                  component="a"
                  href={buildPdfUrl(convId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="폴리오 PDF 다운로드"
                  sx={{
                    px: `${tokens.spacing[5]}px`,
                    minHeight: 42,
                    borderRadius: `${tokens.radius.xl}px`,
                    border: `1px solid ${BORDER}`,
                    bgcolor: PAPER0,
                    color: INK_MID,
                    fontSize: 13,
                    fontWeight: 700,
                    '&:focus-visible': {
                      outline: `2px solid ${BRAND}`,
                      outlineOffset: `${tokens.spacing[1]}px`,
                    },
                  }}
                >
                  PDF 다운로드
                </ButtonBase>
              )}
            </>
          )}
        </Stack>

        {/* [2026-09-21] 처음부터 다시 — 주 동작(보기/다운로드)과 위계를 분명히 벌리기 위해
            구분선 아래 작은 링크로 둔다(사용자 요청: "하단에 작은 글씨로 다시하기"). */}
        <Box
          sx={{
            mt: `${tokens.spacing[5]}px`,
            pt: `${tokens.spacing[3]}px`,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <ButtonBase
            onClick={onRestart}
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              color: INK_LOW,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              p: '6px 10px',
              borderRadius: `${tokens.radius.s}px`,
              '&:hover': { color: INK_MID },
            }}
          >
            처음부터 다시 만들기
          </ButtonBase>
        </Box>
      </Box>
    </Box>
  );
}

function GeneratingPanel({
  stage,
  fallbackLabel,
}: {
  stage: string | null;
  fallbackLabel?: string;
}): JSX.Element {
  // [2026-09-21] stage 가 아직 안 내려온 초기 구간에서는 첫 단계를 진행 중으로 본다.
  // [이유] -1 이면 네 단계가 전부 회색이라 "아무것도 안 하는 중"처럼 보였다(실측: 폴링 첫 응답 전 수 초).
  const foundIdx = STAGE_STEPS.findIndex((s) => s.key === stage);
  const activeIdx = foundIdx >= 0 ? foundIdx : 0;
  const current = activeIdx >= 0 ? STAGE_STEPS[activeIdx] : undefined;
  // 진행률: 현재 단계의 '절반쯤 진행됨'으로 표현 — 단계 전환 때 바가 뒤로 가지 않게 한다
  const progress = activeIdx >= 0 ? ((activeIdx + 0.5) / STAGE_STEPS.length) * 100 : 6;

  return (
    <Box
      aria-busy="true"
      aria-label="폴리오 생성 중"
      sx={{ display: 'flex', flexDirection: 'column', gap: `${tokens.spacing[4]}px` }}
    >
      {/* 현재 단계 헤드라인 — key 를 단계로 주어 전환될 때마다 페이드-업이 다시 재생된다 */}
      <Box>
        <Typography
          key={current?.key ?? 'fallback'}
          role="status"
          aria-live="polite"
          sx={{
            fontSize: 15,
            fontWeight: 800,
            color: INK_HI,
            letterSpacing: '-0.3px',
            display: 'flex',
            alignItems: 'baseline',
            gap: '2px',
            animation: 'folioRise .42s cubic-bezier(.2,.8,.25,1) both',
            '@keyframes folioRise': {
              from: { opacity: 0, transform: 'translateY(6px)' },
              to: { opacity: 1, transform: 'none' },
            },
          }}
        >
          {current?.label ?? fallbackLabel ?? '폴리오를 그리는 중'}
          {/* 점 세 개가 차례로 떠오르는 말줄임 — "지금 일하는 중"을 가장 값싸게 전달한다 */}
          <Box component="span" aria-hidden sx={{ display: 'inline-flex', gap: '2px', ml: '1px' }}>
            {[0, 1, 2].map((d) => (
              <Box
                key={d}
                component="span"
                sx={{
                  width: 3,
                  height: 3,
                  borderRadius: '50%',
                  bgcolor: BRAND,
                  animation: `folioDot 1.2s ease-in-out ${d * 0.16}s infinite`,
                  '@keyframes folioDot': {
                    '0%, 70%, 100%': { opacity: 0.25, transform: 'translateY(0)' },
                    '35%': { opacity: 1, transform: 'translateY(-3px)' },
                  },
                }}
              />
            ))}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: INK_MID, mt: '3px' }}>
          {current?.hint ?? '조금만 기다려 주세요'}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        aria-label="폴리오 생성 진행률"
        sx={{
          height: 4,
          borderRadius: `${tokens.radius.s}px`,
          bgcolor: PAPER2,
          overflow: 'hidden',
          '& .MuiLinearProgress-bar': {
            borderRadius: `${tokens.radius.s}px`,
            // 단계 전환이 '툭' 끊기지 않도록 여유 있는 이징으로 민다
            transition: 'transform .9s cubic-bezier(.4,0,.2,1)',
            background: `linear-gradient(90deg, ${BRAND}, ${c.accent[500][L]})`,
            position: 'relative',
            // 채워진 구간 위를 흰 빛이 훑고 지나간다 — 멈춰 보이는 구간에도 살아 있다는 신호
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,.55) 50%, transparent 100%)',
              transform: 'translateX(-100%)',
              animation: 'folioSweep 1.8s ease-in-out infinite',
            },
          },
          '@keyframes folioSweep': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      />

      {/* 단계 체크리스트 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${tokens.spacing[2]}px` }}>
        {STAGE_STEPS.map((step, i) => {
          const done = activeIdx >= 0 && i < activeIdx;
          const active = i === activeIdx;
          return (
            <Box
              key={step.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: `${tokens.spacing[2]}px`,
                // 목록이 한꺼번에 나타나지 않고 위에서부터 차례로 들어온다
                animation: `folioRise .4s cubic-bezier(.2,.8,.25,1) ${i * 0.06}s both`,
                '@keyframes folioRise': {
                  from: { opacity: 0, transform: 'translateY(6px)' },
                  to: { opacity: 1, transform: 'none' },
                },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: 'relative',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontWeight: 800,
                  color: done || active ? PAPER0 : INK_LOW,
                  bgcolor: done ? c.accent[600][L] : active ? BRAND : PAPER2,
                  border: done || active ? 'none' : `1px solid ${BORDER}`,
                  // 색·크기 전환을 부드럽게(대기 → 진행 → 완료)
                  transition: 'background-color .45s ease, color .45s ease',
                  // [2026-09-21] 점 자체를 깜빡이던 방식(opacity 펄스)은 '꺼진 것처럼' 보였다.
                  //              점은 그대로 두고 바깥으로 링이 퍼지게 바꿔 살아 있는 느낌만 준다.
                  '&::before': active
                    ? {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        border: `2px solid ${BRAND}`,
                        animation: 'folioRipple 1.6s ease-out infinite',
                      }
                    : undefined,
                  '@keyframes folioRipple': {
                    '0%': { transform: 'scale(1)', opacity: 0.55 },
                    '100%': { transform: 'scale(2.1)', opacity: 0 },
                  },
                }}
              >
                {done && (
                  <Box
                    component="span"
                    sx={{
                      // 완료 표시는 톡 튀어나오게 — 단계가 넘어간 걸 눈으로 잡아준다
                      animation: 'folioPop .34s cubic-bezier(.34,1.56,.64,1) both',
                      '@keyframes folioPop': {
                        from: { transform: 'scale(0)', opacity: 0 },
                        to: { transform: 'scale(1)', opacity: 1 },
                      },
                    }}
                  >
                    ✓
                  </Box>
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: active ? 800 : 500,
                  color: done ? INK_MID : active ? INK_HI : INK_LOW,
                  transition: 'color .45s ease',
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* 결과 스켈레톤 — 무엇이 만들어지는 중인지 형태로 암시 */}
      <Box
        aria-hidden
        sx={{ display: 'flex', flexDirection: 'column', gap: `${tokens.spacing[2]}px`, mt: '2px' }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              borderRadius: `${tokens.radius.m}px`,
              border: `1px solid ${BORDER}`,
              bgcolor: PAPER0,
              p: `${tokens.spacing[3]}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              opacity: 1 - i * 0.22,
              animation: `folioRise .45s cubic-bezier(.2,.8,.25,1) ${0.1 + i * 0.08}s both`,
              '@keyframes folioRise': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1 - i * 0.22, transform: 'none' },
              },
            }}
          >
            <Box
              sx={{
                height: 8,
                width: `${52 - i * 8}%`,
                borderRadius: 999,
                background: `linear-gradient(90deg, ${PAPER2} 25%, ${BORDER} 37%, ${PAPER2} 63%)`,
                backgroundSize: '400% 100%',
                animation: `folioShimmer 1.6s linear ${i * 0.18}s infinite`,
                '@keyframes folioShimmer': {
                  '0%': { backgroundPosition: '100% 0' },
                  '100%': { backgroundPosition: '-100% 0' },
                },
              }}
            />
            {['84%', '66%'].map((w, line) => (
              <Box
                key={w}
                sx={{
                  height: 6,
                  width: w,
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${PAPER2} 25%, ${BORDER} 37%, ${PAPER2} 63%)`,
                  backgroundSize: '400% 100%',
                  animation: `folioShimmer 1.6s linear ${i * 0.18 + 0.1 + line * 0.08}s infinite`,
                }}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function ChatPage(): JSX.Element {
  const { viewport } = useShell();
  return viewport.isMobile ? <ChatMobile /> : <ChatDesktop />;
}

// [목적] 데스크톱 채팅 + 우측 생성 상태 패널 split 레이아웃
// [이유] 좌측은 대화 버블 목록, 우측은 phase==='generating' 일 때만 단계 라벨 + 진행 바를 보여준다
//        (D-6 폴링 단계로 진행률을 대체 — SSE 섹션 스트리밍 없음).
// [사이드 이팩트] useChatFlow 의 send/answerClarify/confirmGenerate/cancelGeneration 을 authGuard 로 래핑.
// [수정 시 고려사항] 우측 패널은 phase==='chat' 이면 정적 안내만 표시(생성 전 정보 없음).
function ChatDesktop(): JSX.Element {
  const { viewport, openSidebar } = useShell();
  const { guard: authGuard, isPromptOpen, closePrompt, pendingIntent } = useAuthGate('chat.create');
  const flow = useChatFlow();
  const { data: concepts, isLoading: conceptsLoading, error: conceptsError } = useConcepts();
  // [목적] 칩 영역 노출 조건 — 로딩 중이거나(스켈레톤), 성공 + 1건 이상일 때만 노출
  // [이유] HomePage.tsx showFestivalSection 과 동일 패턴 — 실패/빈 배열이면 칩 영역 자체를 숨겨
  //        채팅 입력 흐름(대화 시작)에는 영향이 없게 한다.
  const showConceptChips = conceptsLoading || (!conceptsError && (concepts?.length ?? 0) > 0);

  // fe-home-chat-prefill: HomePage.tsx 가 navigate(`/chat?q=...`) 로 전달한 자연어를 입력창 시드로 복원.
  // 마운트 시 1회만 읽음(useState 초기값 함수) — 이후 URL 변경엔 반응하지 않는다(자동 전송 없음).
  const [searchParams] = useSearchParams();
  const [input, setInput] = useState(() => searchParams.get('q') ?? '');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // [trip-guide] 단계형 가이드 상태 — 좌측 TripGuide 와 우측 GuidePreview 가 공유한다.
  // [이유] 답변 1건마다 우측 카드가 채워지는 구조라 상태를 페이지가 들고 양쪽에 내려준다.
  // [수정 시 고려사항] 대화가 시작되면(flow.convId) 가이드는 자동으로 사라지고 기존 채팅 UI 로 돌아간다 —
  //                   guideOpen 은 '그냥 한 줄로 쓸래요' 전환만 담당.
  // ?q= 로 홈에서 문장을 들고 온 경우에만 가이드를 건너뛴다(그 문장을 바로 다듬어 보내는 흐름).
  const [guideSkippedByQuery] = useState(() => Boolean(searchParams.get('q')));
  const [guideAnswers, setGuideAnswers] = useState<GuideAnswers>({});
  const [guideCompleted, setGuideCompleted] = useState(false);
  /** 가이드로 시작한 흐름인지 — true 면 채팅 로그/입력창을 끝까지 숨긴다 */
  const [guideDriven, setGuideDriven] = useState(false);
  const [guideCollapsed, setGuideCollapsed] = useState(false);
  // [2026-09-21 QA] 채팅을 보내면 가이드가 통째로 사라져 고른 값이 날아간 것처럼 보였다 —
  // 대화가 시작돼도 가이드는 그대로 두고(선택값 유지, 이어서 선택 가능) 높이만 줄여 대화 로그와 함께 쓴다.
  // [2026-09-21] 가이드 흐름에서는 생성 중에도 좌측에 가이드(선택 요약)를 유지한다.
  // [이유] 생성 중 가이드를 숨겼더니 좌측이 빈 화면이 돼, 급히 좌측에도 진행 패널을 그렸다가
  //        우측 패널과 똑같은 것이 두 번 나오는 꼴이 됐다(사용자 피드백). 진행 표시는 우측 하나로 두고,
  //        좌측은 "내가 고른 것"을 계속 보여준다.
  const showGuide = !guideSkippedByQuery && (flow.phase !== 'generating' || guideDriven);
  // GREETING 외 메시지가 생겼거나 convId 가 잡히면 대화가 시작된 것으로 본다
  const chatStarted = flow.msgs.length > 1 || Boolean(flow.convId);
  const guideAnsweredCount = GUIDE_STEPS.filter((step) =>
    isGuideStepAnswered(step.key, guideAnswers),
  ).length;

  /**
   * [목적] 가이드 완성 문장을 그대로 전송
   * [이유] 2026-09-21 사용자 요청 — 가이드 진행 중에는 채팅 입력창을 숨기므로, 완성 버튼이 곧 전송이다.
   *        전송 자체는 기존 경로(authGuard → flow.send)를 그대로 타 로그인 게이트/세션 처리를 재사용한다.
   * [사이드 이팩트] 전송 후 대화가 시작되면 채팅 로그/입력창이 드러나고 가이드는 위쪽에 접힌 채 남는다.
   */
  const handleGuideSubmit = useCallback(
    (sentence: string): void => {
      authGuard(() => {
        setInput('');
        setGuideDriven(true);
        void flow.send(sentence);
      });
    },
    [authGuard, flow],
  );

  // [2026-09-21] 가이드로 시작한 흐름에서는 채팅 화면을 아예 띄우지 않는다(사용자 요청).
  // [이유] 가이드에서 마지막 질문("꼭 넣고 싶은 게 있나요?")까지 받았는데, 전송 직후 BE 의 최종 자유
  //        질문 버블과 생성 확인 버블이 채팅으로 뜨면서 화면이 갑자기 채팅으로 바뀌었다 — 흐름이 끊긴다.
  //        가이드가 이미 모든 답을 갖고 있으므로 남은 두 단계를 자동으로 통과시킨다.
  // [사이드 이팩트] flow.send / flow.confirmGenerate 를 자동 호출한다 — 사용자가 누른 것과 동일 경로.
  // [수정 시 고려사항] 같은 메시지에 두 번 반응하지 않도록 처리한 메시지 인덱스를 ref 로 기억한다.
  //                   BE 최종 질문 문구가 바뀌면 아래 정규식도 함께 갱신할 것(gap-eval buildAskQuestion 계열).
  const autoStepRef = useRef(-1);
  useEffect(() => {
    if (!guideDriven || flow.sending || flow.phase === 'generating') return;
    const idx = flow.msgs.length - 1;
    if (idx < 0 || idx === autoStepRef.current) return;
    const last = flow.msgs[idx];
    if (!last) return;

    if (last.role === 'confirm') {
      autoStepRef.current = idx;
      void flow.confirmGenerate();
      return;
    }
    if (last.role === 'bot' && /추가하고 싶은|더 알려주실/.test(last.text)) {
      autoStepRef.current = idx;
      // 가이드 마지막 단계에서 이미 받았다 — 없으면 '없어요'로 통과시킨다
      void flow.send(guideAnswers.detail?.trim() || '없어요');
    }
  }, [guideDriven, flow, guideAnswers.detail]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 1e9, behavior: 'smooth' });
  }, [flow.msgs, flow.sending]);

  const isBusy = flow.sending || flow.phase === 'generating';

  const handleSend = useCallback(
    (text?: string): void => {
      const t = text ?? input;
      authGuard(() => {
        setInput('');
        void flow.send(t);
      });
    },
    [authGuard, flow, input],
  );

  /**
   * [목적] 추천 컨셉 칩 클릭 — samplePrompt 를 전송하고 concept 선택값을 훅에 기록
   * [이유] handleSend 와 동일한 authGuard 게이트를 거쳐야 한다(게스트 우회 방지, D-9 유지).
   * [사이드 이팩트] flow.sendConceptChip 이 send() 재사용 + selectedConcept 로컬 기록.
   * [수정 시 고려사항] 없음.
   */
  const handleConceptChip = useCallback(
    (concept: Concept): void => {
      authGuard(() => {
        setInput('');
        void flow.sendConceptChip(concept.samplePrompt, concept.id);
      });
    },
    [authGuard, flow],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * [목적] session-expired 버블 "새 대화 시작" 클릭 — 훅 상태 리셋 + 실패 직전 원문을 입력창에 복원
   * [이유] G1-2 AC: "새 대화 시작 + 마지막 입력 복원" — 사용자가 재타이핑 없이 바로 재전송할 수 있게 한다.
   * [사이드 이팩트] setInput(restoreText) — restoreText 가 빈 문자열(버튼 클릭 경로)이면 입력창은 그대로 비워짐.
   * [수정 시 고려사항] 없음.
   */
  const handleStartNewConversation = useCallback(
    (restoreText: string): void => {
      flow.startNewConversation();
      setInput(restoreText);
    },
    [flow],
  );

  const stageLabel = flow.generation.stage ? STAGE_LABELS[flow.generation.stage] : undefined;

  /**
   * [목적] 오른쪽 패널을 고정 예시 카드 대신 실제 주고받은 대화로 채우기 위한 턴 스택 도출
   * [이유] 2026-09-20 디자인 QA: "오른쪽이 실제 대화와 무관한 고정 예시(속초 바다 한 잔 · 2박 3일)를 계속
   *        보여준다", "마지막 답변 1개로 덮어쓰지 말고 쌓여가는 스택으로 보여달라"는 피드백을 반영한다.
   *        서버 계약상 state 는 z.unknown() 으로 불투명 처리되어 있어(rag.client.ts 주석, NewFolioPage.tsx
   *        동일 컨벤션 참조 — "state 대신 이미 안전한 타입만으로 배지를 계산한다") 이를 베떡으로 파싱
   *        하지 않고, 이미 화면에 놀리는 안전한 ChatMsg[](flow.msgs)만으로 me→bot 짝을 묶어 실제
   *        주고받은 내용을 그대로 보여준다(여행지/인원/기간 등 구조화된 이름으로 재구성하지 않음 — BE 가
   *        구분된 슬롯을 노출하지 않는 한 프론트에서 슬롯명 라벨을 만들어내는 것은 허구의 정보 생산이다).
   * [사이드 이팩트] 없음(순수 파생 값) — flow.msgs 가 바뀐 때만 재계산.
   * [수정 시 고려사항] 구조화된 슬롯(여행지/인원/기간 등) 단위 표시가 필요해지면 BE TurnResponse 에
   *        구조화된 슬롯 필드가 추가되어야 한다(별도 Spec/API 변경 필요 — 이 패치 범위 밖).
   */
  const conversationTurns = useMemo(() => buildTurnStack(flow.msgs), [flow.msgs]);

  // [2026-09-21] 가이드 흐름에서 채팅 로그를 숨기므로, 결과(PDF 준비/실패) 버블만 따로 꺼내 좌측에 그린다.
  // [2026-09-21] 채팅 로그가 실제로 보이는지 — 가이드 높이 계산과 로그 display 가 이 값을 공유한다.
  const showChatLog = !(showGuide && !chatStarted) && !guideDriven;

  const guideResultMsg = useMemo(
    () => flow.msgs.find((m) => m.role === 'pdf-ready' || m.role === 'pdf-failed'),
    [flow.msgs],
  );
  const hasConversation = conversationTurns.length > 0;

  return (
    <>
      <TopBar
        title="새 폴리오"
        breadcrumb="NEW CANVAS"
        mobile={viewport.isMobile}
        onBurger={openSidebar}
      />
      <Box sx={{ display: 'flex', height: 'calc(100vh - 54px)', bgcolor: PAPER1 }}>
        {/* LEFT — 채팅 */}
        <Box
          sx={{
            flex: '0 0 44%',
            maxWidth: 560,
            borderRight: `1px solid ${BORDER}`,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: PAPER0,
          }}
        >
          {/* [목적] 메시지 목록 — 내용이 적어도 아래(입력창 쪽)에 붙여 쌓는다
              [이유] 2026-09-18 QA(M-5): 상단 정렬이라 인사 메시지 1개 뒤로 약 650px 공백이 생겨
                     화면이 비어 보였다. 채팅 관례대로 아래에서 위로 쌓이게 한다.
              [사이드 이팩트] 컨테이너가 flex column 이 되므로 자식 버블이 flex item 이 된다 —
                              세로 찌그러짐 방지를 위해 flexShrink:0 을 함께 지정.
              [수정 시 고려사항] justifyContent:'flex-end' 대신 첫 자식 mt:auto 를 쓰는 이유는
                                 스크롤 컨테이너에서 flex-end 가 위쪽 오버플로를 잘라먹는 브라우저
                                 이슈를 피하기 위함이다. 되돌리지 말 것. */}
          {showGuide && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                // [2026-09-21] 가이드를 줄이는 조건 = "아래 채팅 로그가 실제로 보일 때" 하나뿐이다.
                // [이유] 그동안 조건을 chatStarted 나 결과 유무로 잡았다가, 로그가 숨겨진 상태에서도
                //        가이드가 48%/70% 로 줄어 아래가 통째로 빈 화면이 되는 일이 반복됐다(사용자 피드백 3회).
                //        로그 표시 조건(아래 role="log" 박스의 display)과 **같은 식**을 쓴다 — 두 값이
                //        어긋나면 또 빈 공간이 생기므로 한쪽만 고치지 말 것.
                flex: showChatLog ? '0 1 auto' : 1,
                maxHeight: showChatLog ? '48%' : 'none',
                borderBottom: showChatLog ? `1px solid ${BORDER}` : 'none',
              }}
            >
              {chatStarted && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: `${tokens.spacing[2]}px ${tokens.spacing[6]}px 0` }}
                >
                  <Typography sx={{ fontSize: 11.5, color: INK_MID, fontWeight: 600 }}>
                    여행 설계 가이드 · {guideAnsweredCount}/{GUIDE_STEPS.length} 선택됨
                  </Typography>
                  <ButtonBase
                    onClick={() => setGuideCollapsed((v) => !v)}
                    sx={{ fontSize: 11.5, color: BRAND_TEXT, p: `${tokens.spacing[1]}px` }}
                  >
                    {guideCollapsed ? '펼치기 ▾' : '접기 ▴'}
                  </ButtonBase>
                </Stack>
              )}
              {!(chatStarted && guideCollapsed) && (
                <TripGuide
                  answers={guideAnswers}
                  onAnswersChange={setGuideAnswers}
                  onSubmit={handleGuideSubmit}
                  onCompletedChange={setGuideCompleted}
                />
              )}
            </Box>
          )}

          {/* [2026-09-21] 가이드 흐름의 생성 중·결과 화면.
              [이유] guideDriven 에서는 채팅 로그를 숨기는데, 생성이 시작되면 showGuide 도 false 가 돼
                     좌측이 입력창만 남은 흰 화면이 됐다(사용자 피드백). 같은 자리에 진행/결과를 그린다. */}
          <Box
            ref={scrollRef}
            role="log"
            aria-label="대화 메시지 목록"
            sx={{
              // [2026-09-21] 가이드 흐름에서는 대화 로그를 끝까지 숨긴다(생성 상태는 우측 패널이 보여준다)
              display: showChatLog ? 'flex' : 'none',
              flex: 1,
              overflowY: 'auto',
              p: '24px 26px',
              flexDirection: 'column',
              '& > *': { flexShrink: 0 },
              '& > *:first-of-type': { mt: 'auto' },
            }}
          >
            {flow.msgs.map((m, i) => (
              <MessageBubble
                key={i}
                msg={m}
                disabled={isBusy}
                convId={flow.convId}
                onClarify={(label, value) => authGuard(() => void flow.answerClarify(label, value))}
                onConfirmYes={() => authGuard(() => void flow.confirmGenerate())}
                onConfirmNo={() => flow.declineGenerate()}
                onRetry={() => authGuard(() => void flow.confirmGenerate())}
                onStartNewConversation={handleStartNewConversation}
              />
            ))}
            {/* [수정: 디자인 QA 2026-09-20] 메시지 전송 후 turn() 응답이 도착할 때까지(flow.sending)
                봉 버블 자리에 대기 표시를 넣어 "동작 중"임을 명시한다 — 기존에는 phase==='generating'
                (PDF 생성 폴링)에만 진행 표시가 있고, 일반 대화 담변 대기에는 아무 피드백이 없었다.
                scrollRef useEffect 가 이미 flow.sending 을 의존성으로 가지고 있어(상단 참조) 새 노드
                마운트 시에도 자동 스크롤된다. */}
            {flow.sending && <TypingIndicator />}
          </Box>

          {/* [수정: 디자인 개편] 작은 28px 칩 대신 48px 전체폭 버튼으로 확대하고, 라벨(예: "반려동반")만 보이던 것을
              실제 전송될 문장(samplePrompt)을 그대로 보여줘 누르기 전에 무엇이 전송되는지 명확히 한다. */}
          {!showGuide && !flow.convId && showConceptChips && (
            <Box
              sx={{
                p: `${tokens.spacing[2]}px ${tokens.spacing[6]}px 0`,
                display: 'flex',
                flexDirection: 'column',
                gap: `${tokens.spacing[2]}px`,
              }}
            >
              {conceptsLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={48}
                      sx={{ borderRadius: `${tokens.radius.m}px` }}
                    />
                  ))
                : (concepts ?? []).map((concept) => (
                    <ButtonBase
                      key={concept.id}
                      onClick={() => handleConceptChip(concept)}
                      aria-label={`추천 문구: ${concept.samplePrompt}`}
                      disabled={isBusy}
                      sx={{
                        width: '100%',
                        height: '48px',
                        justifyContent: 'flex-start',
                        gap: `${tokens.spacing[2]}px`,
                        bgcolor: PAPER0,
                        border: `1px solid ${BORDER}`,
                        color: INK_HI,
                        borderRadius: `${tokens.radius.m}px`,
                        px: `${tokens.spacing[4]}px`,
                        fontSize: 12.5,
                        fontWeight: 500,
                        textAlign: 'left',
                        '&:hover': { borderColor: BRAND },
                        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
                      }}
                    >
                      <Box component="span" aria-hidden="true" sx={{ fontSize: 16, flexShrink: 0 }}>
                        {concept.emoji}
                      </Box>
                      <Box
                        component="span"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {concept.samplePrompt}
                      </Box>
                    </ButtonBase>
                  ))}
            </Box>
          )}

          {/* 입력 + 취소 버튼
              [2026-09-21] 가이드 진행 중에는 채팅 입력창을 숨긴다 — 단계별 '직접 입력하기'가 자유 입력을
              대신하고, 완성 버튼이 곧 전송이다. 가이드로 시작한 흐름(guideDriven)에서는 전송 뒤에도
              계속 숨긴다(채팅 화면으로 전환되지 않게 — 사용자 요청). 생성 취소만 별도로 노출한다. */}
          <Stack
            direction="row"
            spacing="8px"
            alignItems="flex-end"
            sx={{
              p: '14px 26px 18px',
              // 가이드 흐름에서는 자유 입력을 쓰지 않는다 — 생성 중 '취소'만 남기고 입력창은 숨긴다
              display:
                (showGuide && !chatStarted) || (guideDriven && flow.phase !== 'generating')
                  ? 'none'
                  : 'flex',
            }}
          >
            {flow.phase === 'generating' && (
              <ButtonBase
                onClick={flow.cancelGeneration}
                aria-label="생성 취소"
                data-testid="cancel-button"
                sx={{
                  bgcolor: PAPER2,
                  border: `1px solid ${BORDER}`,
                  color: INK_MID,
                  borderRadius: `${tokens.radius.m}px`,
                  px: `${tokens.spacing[3]}px`,
                  height: 42,
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                  '&:focus-visible': {
                    outline: `2px solid ${BRAND}`,
                    outlineOffset: `${tokens.spacing[1]}px`,
                  },
                }}
              >
                취소
              </ButtonBase>
            )}
            {/* [2026-09-21] 가이드 흐름에서는 자유 입력을 쓰지 않는다 — 생성 중에도 '취소'만 남긴다.
                입력창이 남아 있으면 "여기에 또 뭘 써야 하나" 하는 혼선을 준다(채팅 제거 요청의 연장). */}
            {!guideDriven && (
              <>
                <TextField
                  multiline
                  fullWidth
                  maxRows={5}
                  minRows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="여행을 자유롭게 묘사해보세요..."
                  disabled={isBusy}
                  inputProps={{ 'aria-label': '여행 자연어 입력' }}
                  inputRef={inputRef}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: PAPER1,
                      borderRadius: `${tokens.radius.m}px`,
                      fontSize: 12.5,
                      color: INK_HI,
                      '& fieldset': { borderColor: BORDER, borderWidth: 1.5 },
                      '&:hover fieldset': { borderColor: BORDER },
                      '&.Mui-focused fieldset': { borderColor: BRAND },
                    },
                  }}
                />
                <ButtonBase
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isBusy}
                  aria-label="전송"
                  sx={{
                    // [수정: 디자인 개편 — 대화로 만들기] 비활성일 때 배경만 PAPER2로 바뀌고 글자색은 여전히 흰색이라
                    // 파란 배경만 없을 뿐 여전히 누를 수 있을 것처럼 보였다. 비활성 글자색도 함께 바꿔 회색 면으로 완전히 보이게 한다.
                    bgcolor: input.trim() && !isBusy ? BRAND : PAPER2,
                    color: input.trim() && !isBusy ? PAPER0 : INK_LOW,
                    borderRadius: `${tokens.radius.m}px`,
                    width: 42,
                    height: 42,
                    fontSize: 16,
                    flexShrink: 0,
                    '&:focus-visible': {
                      outline: `2px solid ${BRAND}`,
                      outlineOffset: `${tokens.spacing[1]}px`,
                    },
                  }}
                >
                  ↑
                </ButtonBase>
              </>
            )}
          </Stack>
        </Box>

        {/* RIGHT — 생성 상태 패널 (D-6: SSE 섹션 스트리밍 대신 폴링 단계 라벨) */}
        <Box
          sx={{
            flex: 1,
            p: `${tokens.spacing[6]}px ${tokens.spacing[8]}px`,
            overflowY: 'auto',
            bgcolor: PAPER1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: BRAND,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              letterSpacing: '0.5px',
              mb: `${tokens.spacing[2]}px`,
            }}
          >
            {flow.phase === 'generating'
              ? 'GENERATING...'
              : guideDriven && guideResultMsg
                ? 'FOLIO READY'
                : hasConversation
                  ? 'YOUR TRIP SO FAR'
                  : 'LIVE PREVIEW'}
          </Typography>

          {/* [2026-09-21] 가이드 흐름 완료 화면 — 좌측 하단 말풍선 대신 우측에 결과를 크게 보여준다.
              [이유] 50초 기다린 결과가 좌측 아래 작은 버블로 툭 나오니 완성의 무게가 전달되지 않았다
                     (사용자 피드백). 우측은 지금까지 진행 상황을 보던 자리라 시선이 이미 가 있다. */}
          {guideDriven && guideResultMsg && flow.phase !== 'generating' ? (
            <GuideResultPanel
              msg={guideResultMsg}
              convId={flow.convId}
              onRetry={() => authGuard(() => void flow.confirmGenerate())}
              onRestart={() => {
                flow.startNewConversation();
                setGuideDriven(false);
                setGuideAnswers({});
              }}
            />
          ) : flow.phase === 'generating' ? (
            <GeneratingPanel stage={flow.generation.stage ?? null} fallbackLabel={stageLabel} />
          ) : showGuide ? (
            /* [trip-guide] 가이드 진행 중 — 답변마다 카드가 하나씩 채워지는 미리보기.
               [2026-09-21 QA] 대화를 시작해도 가이드 선택 결과가 사라지지 않게, 대화 스택을 지우지 않고
               미리보기 아래에 이어서 쌓는다(좌측 가이드가 유지되는 것과 짝을 맞춘다). */
            <Stack spacing={`${tokens.spacing[5]}px`}>
              <GuidePreview answers={guideAnswers} completed={guideCompleted} />
              {hasConversation && (
                <ConversationStack turns={conversationTurns} sending={flow.sending} />
              )}
            </Stack>
          ) : hasConversation ? (
            <ConversationStack turns={conversationTurns} sending={flow.sending} />
          ) : (
            // [수정: 디자인 개편] 안내 문구만 보이던 것을, 실제로 무엇이 만들어지는지 예시로 보여줘
            // "생성 버튼을 누르면 이런 게 나옵니다" 기대를 미리 전달한다(실제 데이터가 아니라 예시임을 명시).
            // [수정: 디자인 QA 2026-09-20] hasConversation(사용자 발화가 1개라도 있음)이면 이 고정 예시 대신
            // 위 ConversationStack(실제 대화)이 렌더된다 — 이 예시 블록은 대화 시작 전(GREETING만 있을 때)에만 보인다.
            <Box>
              <Typography
                sx={{ fontSize: 12, color: INK_LOW, lineHeight: 1.6, mb: `${tokens.spacing[4]}px` }}
              >
                대화를 시작하면 이런 폴리오가 만들어져요 — 예시를 미리 보여드려요.
              </Typography>
              <Box
                aria-hidden="true"
                sx={{
                  bgcolor: PAPER0,
                  border: `1px solid ${BORDER}`,
                  borderRadius: `${tokens.radius.m}px`,
                  p: `${tokens.spacing[4]}px`,
                  opacity: 0.72,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK_HI, mb: '2px' }}>
                  속초 바다 한 잔 · 2박 3일
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: INK_LOW, mb: `${tokens.spacing[3]}px` }}>
                  친구와 함께하는 해변의 하루
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    mb: `${tokens.spacing[3]}px`,
                  }}
                >
                  {[
                    ['10:30', '테라로사 카페'],
                    ['14:00', '경포 해변 산책'],
                    ['17:00', '스테이 체크인'],
                  ].map(([time, place]) => (
                    <Box key={time} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 9.5,
                          fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                          color: BRAND,
                          width: 34,
                          flexShrink: 0,
                        }}
                      >
                        {time}
                      </Box>
                      <Box component="span" sx={{ fontSize: 11, color: INK_MID }}>
                        {place}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: '4px', mb: `${tokens.spacing[2]}px` }}>
                  {[40, 27, 20, 13].map((pct, i) => (
                    <Box
                      key={i}
                      sx={{
                        height: 5,
                        width: `${pct}%`,
                        borderRadius: '3px',
                        bgcolor: i === 0 ? BRAND : PAPER2,
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontSize: 10, color: INK_LOW }}>
                  🗺 지도 · 💰 예산 분배 · 📷 무드보드
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <LoginPromptModal open={isPromptOpen} intent={pendingIntent} onClose={closePrompt} />

      <style>{`@keyframes gwBlink{0%,80%,100%{opacity:.3}40%{opacity:1}}`}</style>
    </>
  );
}

/**
 * [목적] 봇 버블 자리에 보여주는 응답 대기 인디케이터 — turn() 응답 도착 전까지 점 3개를 순차적으로 깜박임
 * [이유] 2026-09-20 디자인 QA: 메시지 전송 후 응답이 올 때까지 화면이 전혀 반응하지 않아 "먹통"처럼
 *        보였다는 피드백 — 기존 MessageBubble('bot') 과 동일한 아바타/버블 톤(PAPER2, radius.l,
 *        SHADOW_CARD)을 재사용해 "곧 응답 진행 중"임을 자연스럽게 예고한다. 점 애니메이션은 이 파일
 *        하단에 이미 정의된 전역 @keyframes gwBlink 를 그대로 재사용(신규 keyframes 미생성).
 * [사이드 이팩트] 없음(순수 표시 컴포넌트) — flow.sending 이 true 인 동안만 호출부에서 조건부 렌더.
 * [수정 시 고려사항] prefers-reduced-motion 사용자 배려는 기존 gwBlink 정의에 위임(별도 미디어쿼리 미추가).
 */
function TypingIndicator(): JSX.Element {
  return (
    <Box
      sx={{ display: 'flex', mb: `${tokens.spacing[3]}px` }}
      role="status"
      aria-live="polite"
      aria-label="답변을 기다리는 중"
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${BRAND}, ${c.accent[500][L]})`,
          color: PAPER0,
          fontSize: 12,
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: `${tokens.spacing[2]}px`,
          flexShrink: 0,
          boxShadow: SHADOW_CARD,
        }}
      >
        ✦
      </Box>
      <Box
        sx={{
          bgcolor: PAPER2,
          borderRadius: `${tokens.radius.l}px`,
          px: `${tokens.spacing[3]}px`,
          py: `${tokens.spacing[3]}px`,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          boxShadow: SHADOW_CARD,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            aria-hidden="true"
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: INK_LOW,
              animation: 'gwBlink 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.18}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

interface ConversationStackProps {
  turns: ConversationTurn[];
  /** true 면 가장 마지막 턴이 아직 응답을 기다리는 중 — 카드 하단에 대기 표시를 덧붙인다 */
  sending: boolean;
}

/**
 * [목적] 우측 "YOUR TRIP SO FAR" 패널 본체 — 실제로 주고받은 대화를 카드 스택으로 누적 표시
 * [이유] 2026-09-20 디자인 QA: "오른쪽에 반영된 답변이 마지막 1개로 덮어써진다, 스택으로 쌓였으면
 *        좋겠다"는 피드백 — 매 턴마다 새 카드를 아래로 추가해 기존 턴이 사라지지 않고 계속 보이게 한다.
 * [사이드 이팩트] 없음(순수 표시 컴포넌트).
 * [수정 시 고려사항] answer===null(아직 응답 대기)인 마지막 턴은 스켈레톤 줄로 표시. 구조화된 슬롯
 *                   이름(여행지/인원/기간 등) 단위 카드가 아니라 실제 질문·답변 원문 그대로임에 유의
 *                   (buildTurnStack 주석 참조 — opaque state 파싱을 피하기 위한 의도적 설계).
 */
function ConversationStack({ turns, sending }: ConversationStackProps): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${tokens.spacing[2]}px` }}>
      {turns.map((turn, i) => (
        <Box
          key={i}
          sx={{
            bgcolor: PAPER0,
            border: `1px solid ${BORDER}`,
            borderRadius: `${tokens.radius.m}px`,
            p: `${tokens.spacing[3]}px`,
          }}
        >
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              color: BRAND,
              letterSpacing: '0.5px',
              mb: '4px',
            }}
          >
            TURN {String(i + 1).padStart(2, '0')}
          </Typography>
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: INK_HI, lineHeight: 1.55, mb: '6px' }}
          >
            {turn.request}
          </Typography>
          {turn.answer ? (
            <Typography
              sx={{ fontSize: 11.5, color: INK_MID, lineHeight: 1.7, whiteSpace: 'pre-line' }}
            >
              {turn.answer}
            </Typography>
          ) : (
            i === turns.length - 1 &&
            sending && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }} aria-hidden="true">
                <Box sx={{ height: 7, width: '85%', borderRadius: '4px', bgcolor: PAPER2 }} />
                <Box sx={{ height: 7, width: '60%', borderRadius: '4px', bgcolor: PAPER2 }} />
              </Box>
            )
          )}
        </Box>
      ))}
    </Box>
  );
}

// ── 공용 버블 렌더러 ──────────────────────────────────────────────────────────

interface MessageBubbleProps {
  msg: ChatMsg;
  disabled: boolean;
  convId: string | null;
  onClarify: (label: string, value: unknown) => void;
  onConfirmYes: () => void;
  onConfirmNo: () => void;
  onRetry: () => void;
  /** G1-2: session-expired 버블 "새 대화 시작" — restoreText(실패 직전 원문, 없으면 '')를 인자로 전달 */
  onStartNewConversation: (restoreText: string) => void;
}

/**
 * [목적] ChatMsg 1건 → 버블 JSX 변환 (me/bot/clarify/confirm/pdf-ready/pdf-failed/session-expired 분기)
 * [이유] useChatFlow 가 소유한 대화 상태를 시각화하는 유일한 지점 — ChatDesktop 전용(ChatMobile 은
 *        별도 비대칭 radius 스타일을 쓰므로 자체 렌더러를 둔다, 모바일 디자인 언어 분리 관행 유지).
 * [사이드 이팩트] 없음(순수 표시 컴포넌트) — 클릭 시 부모가 전달한 콜백만 호출.
 * [수정 시 고려사항] disabled 는 sending||phase==='generating' 기준 — 버블 종류 무관 전체 적용.
 *                   clarifyOptions 빈 배열이면 칩 미표시(텍스트만).
 */
/**
 * [목적] 봇 텍스트 버블 — 한 글자씩 나타나는 타이프라이터 연출 + 클릭 시 즉시 완성
 * [이유] 2026-09-20 chat-typewriter(사용자 요청): 답변이 통째로 "툽" 나타나 대화가 기계적이었다.
 *        POST /rag/:id/turn 은 완성된 텍스트를 한 번에 주므로(토큰 스트리밍 아님) 클라이언트에서
 *        점진 렌더해 말하는 리듬만 재현한다 — 실제 대기시간이 줄지는 않는다(useTypewriter 주석 참조).
 * [사이드 이팩트] 타이핑 중에는 버블 높이가 계속 커진다 — 상위 리스트의 자동스크롤 useEffect 는
 *                 flow.msgs 변화에만 반응하므로, 긴 답변에서는 타이핑 도중 마지막 줄이 잠시 가려질 수 있다
 *                 (사용자가 버블을 누르면 즉시 완성되며 해소된다).
 * [수정 시 고려사항] 커서는 장식이므로 aria-hidden — 스크린리더가 "|"를 읽지 않도록 한다.
 *                   버블 자체는 aria-live 를 달지 않는다 — 상위 컨테이너가 이미 role="log" 이라
 *                   중복 낝독된다. 단, 글자단위 갱신이 log 에 전부 통보되지 않도록 완성 텍스트를
 *                   별도 sr-only 로 두는 방안은 향후 접근성 QA 에서 검토할 것.
 */
function BotBubble({ text, avatar }: { text: string; avatar: JSX.Element }): JSX.Element {
  const { displayed, isTyping, skip } = useTypewriter(text);

  return (
    <Box sx={{ display: 'flex', mb: `${tokens.spacing[3]}px` }}>
      {avatar}
      <Box
        onClick={isTyping ? skip : undefined}
        sx={{
          maxWidth: '78%',
          bgcolor: PAPER2,
          color: INK_HI,
          borderRadius: `${tokens.radius.l}px`,
          px: `${tokens.spacing[3]}px`,
          py: `${tokens.spacing[2]}px`,
          fontSize: 12.5,
          lineHeight: 1.55,
          whiteSpace: 'pre-line',
          boxShadow: SHADOW_CARD,
          cursor: isTyping ? 'pointer' : 'default',
        }}
      >
        {displayed}
        {isTyping && (
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              display: 'inline-block',
              width: '2px',
              height: '1em',
              verticalAlign: '-2px',
              ml: '1px',
              borderRadius: '1px',
              bgcolor: BRAND,
              animation: 'gcTypeCaret .9s steps(1,end) infinite',
              '@keyframes gcTypeCaret': {
                '0%, 45%': { opacity: 1 },
                '50%, 95%': { opacity: 0 },
                '100%': { opacity: 1 },
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
}

function MessageBubble({
  msg,
  disabled,
  convId,
  onClarify,
  onConfirmYes,
  onConfirmNo,
  onRetry,
  onStartNewConversation,
}: MessageBubbleProps): JSX.Element {
  const avatar = (
    <Box
      aria-hidden="true"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${BRAND}, ${c.accent[500][L]})`,
        color: PAPER0,
        fontSize: 12,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: `${tokens.spacing[2]}px`,
        flexShrink: 0,
        boxShadow: SHADOW_CARD,
      }}
    >
      ✦
    </Box>
  );

  if (msg.role === 'me') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: `${tokens.spacing[3]}px` }}>
        <Box
          sx={{
            maxWidth: '78%',
            bgcolor: BRAND,
            color: PAPER0,
            borderRadius: `${tokens.radius.l}px`,
            px: `${tokens.spacing[3]}px`,
            py: `${tokens.spacing[2]}px`,
            fontSize: 12.5,
            lineHeight: 1.55,
            whiteSpace: 'pre-line',
          }}
        >
          {msg.text}
        </Box>
      </Box>
    );
  }

  if (msg.role === 'bot') {
    return <BotBubble text={msg.text} avatar={avatar} />;
  }

  if (msg.role === 'clarify') {
    return (
      <Box sx={{ display: 'flex', mb: `${tokens.spacing[3]}px` }}>
        {avatar}
        <Box
          role="group"
          aria-label="선택지 확인"
          sx={{
            maxWidth: '78%',
            bgcolor: PAPER2,
            borderRadius: `${tokens.radius.l}px`,
            px: `${tokens.spacing[4]}px`,
            py: `${tokens.spacing[3]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
            boxShadow: SHADOW_CARD,
          }}
        >
          {msg.text && (
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI, m: 0 }}>
              {msg.text}
            </Typography>
          )}
          {(msg.clarifyOptions ?? []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${tokens.spacing[1]}px` }}>
              {(msg.clarifyOptions ?? []).map((opt, idx) => (
                <ButtonBase
                  key={idx}
                  onClick={() => onClarify(opt.label, opt.value)}
                  disabled={disabled}
                  aria-label={opt.label}
                  sx={{
                    borderRadius: `${tokens.radius.xl}px`,
                    px: `${tokens.spacing[3]}px`,
                    py: `${tokens.spacing[1]}px`,
                    fontSize: 12,
                    fontWeight: 500,
                    bgcolor: PAPER0,
                    color: INK_MID,
                    border: `1px solid ${BORDER}`,
                    opacity: disabled ? 0.48 : 1,
                    '&:focus-visible': {
                      outline: `2px solid ${BRAND}`,
                      outlineOffset: `${tokens.spacing[1]}px`,
                    },
                  }}
                >
                  {opt.label}
                </ButtonBase>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (msg.role === 'confirm') {
    return (
      <Box sx={{ display: 'flex', mb: `${tokens.spacing[3]}px` }}>
        {avatar}
        <Box
          role="group"
          aria-label="폴리오 생성 확인"
          sx={{
            maxWidth: '78%',
            bgcolor: PAPER2,
            borderRadius: `${tokens.radius.l}px`,
            px: `${tokens.spacing[4]}px`,
            py: `${tokens.spacing[3]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
            boxShadow: SHADOW_CARD,
          }}
        >
          <Typography
            sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI, fontWeight: 500, m: 0 }}
          >
            {msg.text}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${tokens.spacing[1]}px` }}>
            <ButtonBase
              onClick={onConfirmYes}
              disabled={disabled}
              aria-label="네, 만들어주세요"
              data-testid="confirm-yes-button"
              sx={{
                borderRadius: `${tokens.radius.xl}px`,
                px: `${tokens.spacing[3]}px`,
                py: `${tokens.spacing[1]}px`,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: disabled ? PAPER2 : BRAND,
                color: disabled ? INK_LOW : PAPER0,
                border: `1px solid ${disabled ? BORDER : BRAND}`,
                opacity: disabled ? 0.48 : 1,
                '&:focus-visible': {
                  outline: `2px solid ${BRAND}`,
                  outlineOffset: `${tokens.spacing[1]}px`,
                },
              }}
            >
              네, 만들어주세요
            </ButtonBase>
            <ButtonBase
              onClick={onConfirmNo}
              disabled={disabled}
              aria-label="조금 더 다듬을래요"
              data-testid="confirm-refine-button"
              sx={{
                borderRadius: `${tokens.radius.xl}px`,
                px: `${tokens.spacing[3]}px`,
                py: `${tokens.spacing[1]}px`,
                fontSize: 12,
                fontWeight: 500,
                bgcolor: 'transparent',
                color: disabled ? INK_LOW : INK_MID,
                border: `1px solid ${disabled ? BORDER : c.line.base[L]}`,
                opacity: disabled ? 0.48 : 1,
                '&:focus-visible': {
                  outline: `2px solid ${BRAND}`,
                  outlineOffset: `${tokens.spacing[1]}px`,
                },
              }}
            >
              조금 더 다듬을래요
            </ButtonBase>
          </Box>
        </Box>
      </Box>
    );
  }

  if (msg.role === 'pdf-ready') {
    return (
      // [2026-09-20] 완성 알림을 일반 말풍선에서 '결과 카드'로 승격.
      // [이유] 50초를 기다린 끝의 결과인데 "폴리오가 준비됐어요! 🎉" 한 줄 + 버튼 두 개를 회색 말풍선으로
      //        툭 던지고 끝나, 완성의 무게가 전혀 전달되지 않았다(사용자 피드백: "이렇게 던지지 말고").
      //        상단 브랜드 그라데이션 바 + 큰 제목 + 안내 문구 + 버튼 위계로 결과물처럼 보이게 한다.
      //        이모지(🎉)는 플랫폼마다 컬러 그림이라 톤이 튀어 제거하고, 완료 체크 마크로 대체했다.
      <Box sx={{ display: 'flex', mb: `${tokens.spacing[3]}px` }}>
        {avatar}
        <Box
          sx={{
            maxWidth: '88%',
            width: '100%',
            bgcolor: PAPER0,
            border: `1px solid ${BORDER}`,
            borderRadius: `${tokens.radius.l}px`,
            overflow: 'hidden',
            boxShadow: SHADOW_CARD,
          }}
        >
          {/* 브랜드 그라데이션 바 — 결과물의 '표지' 역할 */}
          <Box
            aria-hidden
            sx={{ height: 4, background: `linear-gradient(90deg, ${BRAND}, ${c.accent[500][L]})` }}
          />
          <Box
            sx={{
              px: `${tokens.spacing[4]}px`,
              py: `${tokens.spacing[4]}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: `${tokens.spacing[3]}px`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: `${tokens.spacing[2]}px` }}>
              <Box
                aria-hidden
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: c.accent[600][L],
                  color: PAPER0,
                  fontSize: 11,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                ✓
              </Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  color: c.accent[600][L],
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                }}
              >
                FOLIO READY
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{ fontSize: 16, fontWeight: 800, color: INK_HI, letterSpacing: '-0.4px' }}
              >
                폴리오가 완성됐어요
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: INK_MID, mt: '4px', lineHeight: 1.6 }}>
                하루 동선과 장소 정보, 예산까지 한 권으로 정리했어요.
              </Typography>
            </Box>
            {/* [목적] "폴리오 보기" 주 버튼(portfolioId 있을 때) + PDF 다운로드 부가 버튼(항상 유지, G1-1)
              [이유] 딥링크(/folio/:id) 이동을 주 동선으로 승격 — BE 영속 실패(portfolioId null)면 기존처럼
              PDF 다운로드만 노출(하위호환). */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${tokens.spacing[2]}px` }}>
              {msg.portfolioId && (
                <ButtonBase
                  component={RouterLink}
                  to={`/folio/${msg.portfolioId}`}
                  aria-label="완성된 폴리오 보기"
                  sx={{
                    borderRadius: `${tokens.radius.xl}px`,
                    px: `${tokens.spacing[4]}px`,
                    // [2026-09-20] 44px 터치 타겟 규칙 — 결과 카드의 주 동작이라 높이를 확보한다
                    minHeight: 36,
                    fontSize: 12.5,
                    fontWeight: 800,
                    bgcolor: BRAND,
                    color: PAPER0,
                    '&:focus-visible': {
                      outline: `2px solid ${BRAND}`,
                      outlineOffset: `${tokens.spacing[1]}px`,
                    },
                  }}
                >
                  폴리오 보기
                </ButtonBase>
              )}
              {convId && (
                <ButtonBase
                  component="a"
                  href={buildPdfUrl(convId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="폴리오 PDF 다운로드"
                  sx={{
                    borderRadius: `${tokens.radius.xl}px`,
                    px: `${tokens.spacing[4]}px`,
                    fontSize: 12.5,
                    minHeight: 36,
                    fontWeight: msg.portfolioId ? 600 : 800,
                    bgcolor: msg.portfolioId ? 'transparent' : BRAND,
                    color: msg.portfolioId ? INK_MID : PAPER0,
                    border: msg.portfolioId ? `1px solid ${BORDER}` : 'none',
                    '&:focus-visible': {
                      outline: `2px solid ${BRAND}`,
                      outlineOffset: `${tokens.spacing[1]}px`,
                    },
                  }}
                >
                  PDF 다운로드
                </ButtonBase>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (msg.role === 'pdf-failed') {
    return (
      <Box sx={{ display: 'flex', mb: `${tokens.spacing[3]}px` }}>
        {avatar}
        <Box
          role="alert"
          aria-live="assertive"
          sx={{
            maxWidth: '78%',
            bgcolor: PAPER2,
            borderRadius: `${tokens.radius.l}px`,
            px: `${tokens.spacing[4]}px`,
            py: `${tokens.spacing[3]}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${tokens.spacing[2]}px`,
            boxShadow: SHADOW_CARD,
          }}
        >
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI, m: 0 }}>
            {/* [수정: QA-P1-02] msg.text 는 useChatFlow 가 generationErrorMessage() 로 이미 한글 매핑한
                문구다 — BE rag.* 코드를 화면에 그대로 노출하지 않는다. */}
            {msg.text || '폴리오 생성에 실패했어요.'}
          </Typography>
          <ButtonBase
            onClick={onRetry}
            disabled={disabled}
            aria-label="폴리오 생성 다시 시도"
            data-testid="retry-button"
            sx={{
              alignSelf: 'flex-start',
              borderRadius: `${tokens.radius.xl}px`,
              px: `${tokens.spacing[3]}px`,
              py: `${tokens.spacing[1]}px`,
              fontSize: 12,
              fontWeight: 700,
              // [수정: 디자인 개편 — 비활성 버튼은 회색으로] 이전에는 opacity만 낮춰 파란 배경+흰색 글자가 그대로 남아
              // 누를 수 있는 버튼처럼 보였다. 비활성일 때는 회색 면+어두운 글자로 명확히 구분한다.
              bgcolor: disabled ? PAPER2 : BRAND,
              color: disabled ? INK_LOW : PAPER0,
              '&:focus-visible': {
                outline: `2px solid ${BRAND}`,
                outlineOffset: `${tokens.spacing[1]}px`,
              },
            }}
          >
            다시 시도
          </ButtonBase>
        </Box>
      </Box>
    );
  }

  // session-expired (G1-2) — convId 만료/미발견 복구 UI: 안내 문구 + "새 대화 시작"(마지막 입력 복원)
  return (
    <Box sx={{ display: 'flex', mb: `${tokens.spacing[3]}px` }}>
      {avatar}
      <Box
        role="alert"
        sx={{
          maxWidth: '78%',
          bgcolor: PAPER2,
          borderRadius: `${tokens.radius.l}px`,
          px: `${tokens.spacing[4]}px`,
          py: `${tokens.spacing[3]}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: `${tokens.spacing[2]}px`,
          boxShadow: SHADOW_CARD,
        }}
      >
        <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: INK_HI, m: 0 }}>
          대화 세션이 만료됐어요. 새 대화를 시작해 주세요.
        </Typography>
        <ButtonBase
          onClick={() => onStartNewConversation(msg.text)}
          aria-label="새 대화 시작"
          data-testid="start-new-conversation-button"
          sx={{
            alignSelf: 'flex-start',
            borderRadius: `${tokens.radius.xl}px`,
            px: `${tokens.spacing[3]}px`,
            py: `${tokens.spacing[1]}px`,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: BRAND,
            color: PAPER0,
            '&:focus-visible': {
              outline: `2px solid ${BRAND}`,
              outlineOffset: `${tokens.spacing[1]}px`,
            },
          }}
        >
          새 대화 시작
        </ButtonBase>
      </Box>
    </Box>
  );
}
